import { use } from "react";

import Layout from "@bokio/components/Layout/Layout";
import { HelpContext } from "@bokio/contexts/HelpContext/HelpContext";
import { useTopLevelUser } from "@bokio/contexts/TopLevelUserContext/useTopLevelUser";
import { LoadingBox } from "@bokio/designsystem/components/Loading";
import { Box } from "@bokio/elements/Box";
import { useCardExpiryWarning } from "@bokio/hooks/useCardExpiryWarning/useCardExpiryWarning";
import { GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useSettings } from "@bokio/settings/src/scenes/Overview/components/utils/useSettings";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";
import { useCompanyUser, useUser } from "@bokio/shared/state/requests";
import { hasFailedPaymentState } from "@bokio/utils/priceplanUtils";

import { AddFiscalYearModal } from "../AddFiscalYearModal/AddFiscalYearModal";
import { CompanyCommandPalette } from "../CommandPalette/CompanyCommandPalette";
import {
	menuSectionsToPaletteCommand,
	settingsSectionsToPaletteCommand,
} from "../CommandPalette/utils/commandPalette.helpers";
import { CompanyRouteModalQueue } from "../CompanyRouteModalQueue/CompanyRouteModalQueue";
import { ExpiringCardTopBar } from "../FailedPaymentTopBar/ExpiringCardTopBar";
import { FailedPaymentTopBar } from "../FailedPaymentTopBar/FailedPaymentTopBar";
import CompanyMenuProvider from "../Menu/components/CompanyMenuProvider/CompanyMenuProvider";
import Sidebar from "../Sidebar/Sidebar";
import { SveaAuthenticationChecker } from "../SveaAuthenticationChecker/SveaAuthenticationChecker";
import { TestCompanyTopBar } from "../TestCompanyTopBar/TestCompanyTopBar";
import TopBar, { TopBarMode } from "../TopBar/TopBar";
import { CompanyAccessDeniedPage } from "./CompanyAccessDeniedPage/CompanyAccessDeniedPage";
import { HubspotSupportChat } from "./HubspotSupportChat";

import type { ReactNode } from "react";

import * as styles from "./companyRoute.scss";

import CompanySystem = m.Entities.CompanySystem;
import FiscalYearStatus = m.Core.FiscalYearStatus;

const CompanyRouteContent = ({ children }: { children: ReactNode }) => {
	const { companyInfo } = useCompanyInfo();
	const { userStatus, reloadCompanyUser } = useCompanyUser();
	const { isLoadingUser } = useUser();
	const router = useRouter();
	const helpContext = use(HelpContext);

	if (isLoadingUser || !userStatus || !companyInfo) {
		return (
			<>
				<Box>
					<LoadingBox spacingTop />
				</Box>
				<SveaAuthenticationChecker companyId={companyInfo.Id} />
			</>
		);
	}

	const isOnFiscalYearPage =
		router.location && router.location.pathname.startsWith(getRoute("fiscalList", { company: companyInfo.Id }));

	const freeUser = companyInfo.Plan == m.Entities.BokioPlan.Free;
	const blockedDueToFailedPayments =
		companyInfo.BillingPaymentState === m.Entities.BillingSubscriptionPaymentState.BlockedDueToMissingPayment;

	// AM 2024-12-03 If the user does not have company settings access but there is still an active year it should
	// still be possible to view content. For example, agencies should still be able to visit the client's pages.
	const needsFiscalUpdate =
		(userStatus.Access.CompanySettings && companyInfo.FiscalYearStatus === FiscalYearStatus.PromptToCreateNextYear) ||
		companyInfo.FiscalYearStatus === FiscalYearStatus.NoActiveYear;

	if (needsFiscalUpdate && !isOnFiscalYearPage && !freeUser && !blockedDueToFailedPayments) {
		const onFiscalYearCreated = () => reloadCompanyUser();

		return (
			<AddFiscalYearModal
				disableBackgroundClick={true}
				visible={true}
				disabled={true}
				onClose={onFiscalYearCreated}
				autoFocus={true}
				userPermissions={userStatus.Access}
			/>
		);
	}

	return <div ref={ref => helpContext.setScreenshotElement(ref)}>{children}</div>;
};

const CompanyRouteLayout = ({ isDemo, children }: { isDemo: boolean; children: ReactNode }) => {
	const { companyInfo } = useCompanyInfo();
	const { companyUserPermissions } = useCompanyUser();
	const { settings } = useSettings(companyInfo, companyUserPermissions);
	const generalLang = GeneralLangFactory();
	const blockedDueToFailedPayment =
		companyInfo.BillingPaymentState === m.Entities.BillingSubscriptionPaymentState.BlockedDueToMissingPayment;

	return (
		<Layout
			isDemo={isDemo}
			menu={menuProps => (
				<div>
					<TopBar mode={TopBarMode.Company} toggleMenu={menuProps.toggleMenu} />
					<CompanyMenuProvider>
						{sections => (
							<>
								<Sidebar
									expanded={menuProps.expanded}
									onToggle={menuProps.toggleMenu}
									onClose={menuProps.closeMenu}
									sections={sections}
									showBookkeepingButton={companyInfo.Plan !== m.Entities.BokioPlan.Free && !blockedDueToFailedPayment}
								/>
								<CompanyCommandPalette
									commands={menuSectionsToPaletteCommand(generalLang, sections).concat(
										settingsSectionsToPaletteCommand(generalLang, settings),
									)}
								/>
							</>
						)}
					</CompanyMenuProvider>
				</div>
			)}
			content={<CompanyRouteContent>{children}</CompanyRouteContent>}
		/>
	);
};

export const CompanyRoute = ({ children }: { children: ReactNode }) => {
	const { companyInfo } = useCompanyInfo();
	const { hasErrorGettingCompanyUser } = useTopLevelUser();
	const router = useRouter();

	const isOnDashboard =
		!!companyInfo && router.location?.pathname === getRoute("dashboard", { company: companyInfo.Id });
	const { isExpiringSoon, isExpired } = useCardExpiryWarning(
		companyInfo?.Id ?? "",
		companyInfo?.Plan ?? m.Entities.BokioPlan.Free,
		isOnDashboard && !!companyInfo,
	);

	if (hasErrorGettingCompanyUser) {
		return <CompanyAccessDeniedPage />;
	}

	// Do not render anything if company hasn't loaded. If we don't do this we will get problems
	// in menus because they are mounted twice
	if (!companyInfo) {
		return null;
	}

	const isDemo = companyInfo.CompanySystem === CompanySystem.DemoCompany;
	const isTest = companyInfo.CompanySystem === CompanySystem.TestCompany;
	const hasFailedPayment = !isDemo && !isTest && hasFailedPaymentState(companyInfo.BillingPaymentState);
	const isPaidByAgency =
		companyInfo.BillingPaymentState === m.Entities.BillingSubscriptionPaymentState.PaidByAgency ||
		companyInfo.BillingPaymentState === m.Entities.BillingSubscriptionPaymentState.PaidByAgencyAndHaveOwnPlan;
	const showExpiringCard =
		isOnDashboard && !isDemo && !isTest && !hasFailedPayment && !isPaidByAgency && (isExpiringSoon || isExpired);

	if (!isDemo && !isTest && !hasFailedPayment && !showExpiringCard) {
		return (
			<>
				<CompanyRouteLayout isDemo={false}>{children}</CompanyRouteLayout>
				<HubspotSupportChat />
				<CompanyRouteModalQueue />
			</>
		);
	}

	return (
		<div className={styles.companyRouteWrapper}>
			{isTest && <TestCompanyTopBar />}
			{hasFailedPayment && <FailedPaymentTopBar company={companyInfo.Id} />}
			{showExpiringCard && <ExpiringCardTopBar company={companyInfo.Id} isExpired={isExpired} />}
			<CompanyRouteLayout isDemo={isDemo || isTest || hasFailedPayment || showExpiringCard}>
				{children}
			</CompanyRouteLayout>
			<HubspotSupportChat />
			<CompanyRouteModalQueue />
		</div>
	);
};
